import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Popover,
  MenuItem,
  MenuList,
  Box,
  Typography
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import './CustomDropdown.scss';

interface Option {
  label: string;
  value: string;
}

interface GdsDropdownProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

const CustomDropdown: React.FC<GdsDropdownProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Select...',
  disabled = false,
  required = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [announcement, setAnnouncement] = useState('');
  const open = Boolean(anchorEl);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (open) {
        e.preventDefault();
      }
    };

    if (open) {
      window.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [open]);

  const handleClick = () => {
    setAnnouncement('expanded');
    setTimeout(() => {
      setAnchorEl(anchorRef.current);
    }, 50);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (optionValue: string) => {
    onChange(optionValue);
    setAnchorEl(null);
  };

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Box
      className={`gds-dropdown-mui${disabled ? ' disabled' : ''}`}
      sx={{ position: 'relative' }}
    >
      <Typography
        component="span"
        sx={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          width: '1px'
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </Typography>
      {label && (
        <Typography
          variant="caption"
          className={`gds-dropdown-label${open || !!value ? ' floated' : ''}`}
          sx={{
            position: 'absolute',
            top: (open || !!value) ? -18 : 10,
            left: 0,
            transition: 'all 0.2s',
            color: 'text.secondary',
            backgroundColor: 'background.paper',
            px: 0.5
          }}
        >
          {label} {required && '*'}
        </Typography>
      )}
      <Button
        ref={anchorRef}
        role="combobox"
        aria-controls={open ? 'gds-dropdown-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleClick}
        disabled={disabled}
        disableRipple
        disableFocusRipple
        variant="outlined"
        className="gds-dropdown-control"
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        sx={{
          width: '100%',
          fontWeight: 400,
          fontSize: '1rem',
          paddingLeft: 0,
          paddingRight: 0,
          color: 'text.primary',
          justifyContent: 'space-between',
          textTransform: 'none',
          backgroundColor: 'white',
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: 0,
          borderColor: 'grey.400',
          '&:hover': {
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderColor: 'grey.600',
            backgroundColor: 'white'
          }
        }}
      >
        <Typography
          component="span"
          sx={{
            color: value ? 'text.primary' : 'white'
          }}
          aria-label={selectedOption ? `${placeholder} ${selectedOption.label}` : placeholder}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </Typography>
      </Button>
      <Popover
        id="gds-dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        aria-haspopup="true"
        aria-expanded={open}
        onClose={handleClose}
        container={document.body}
        disablePortal={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        PaperProps={{
          style: {
            width: anchorRef.current?.offsetWidth,
            maxHeight: 250
          }
        }}
      >
        <MenuList autoFocusItem={open} id="gds-dropdown-menu-list">
          {options.map((option) => (
            <MenuItem
              key={option.value}
              aria-selected={value === option.value}
              selected={option.value === value}
              onClick={() => handleMenuItemClick(option.value)}
              aria-label={`${option.label} ${value === option.value ? 'selected' : 'not selected'}`}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </Box>
  );
};

export default CustomDropdown;
